import React, { useState, useEffect } from 'react';
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Calendar, Bell, Check, X, AlertCircle } from "lucide-react";
import CalendarService from '../services/CalendarService';
import NotificationService from '../services/NotificationService';
import { toast } from "sonner";

const CalendarIntegration = () => {
  const [calendarStatus, setCalendarStatus] = useState({
    isSupported: false,
    isInitialized: false,
    calendarInfo: null,
    permissions: false
  });
  
  const [notificationStatus, setNotificationStatus] = useState({
    isSupported: false,
    permissions: false
  });

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    checkIntegrationStatus();
  }, []);

  const checkIntegrationStatus = async () => {
    setIsLoading(true);
    
    try {
      // Check calendar service status
      const calendarSupported = CalendarService.isSupported();
      const calendarInfo = CalendarService.getCalendarInfo();
      
      setCalendarStatus({
        isSupported: calendarSupported,
        isInitialized: CalendarService.isInitialized,
        calendarInfo: calendarInfo,
        permissions: calendarSupported
      });

      // Check notification service status
      const notificationSupported = NotificationService.isSupported();
      
      setNotificationStatus({
        isSupported: notificationSupported,
        permissions: notificationSupported
      });

    } catch (error) {
      console.error('Check integration status error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const testCalendarIntegration = async () => {
    setIsLoading(true);
    
    try {
      const testEvent = {
        id: 'test-' + Date.now(),
        title: 'Test Devimana Event',
        description: 'This is a test event from Devimana Calendar app',
        calendar_type: 'gregorian',
        gregorian_date: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Tomorrow
        tithi_info: {
          tithi_name: 'प्रतिपदा',
          paksha: 'शुक्ल पक्ष',
          tithi_progress: 0.25
        }
      };

      // Test calendar integration
      const calendarResult = await CalendarService.addEventToCalendar(testEvent);
      
      if (calendarResult.success) {
        toast.success(`✅ Test event added to ${calendarResult.calendarName}`);
        
        // Test notification scheduling
        const notificationResult = await NotificationService.scheduleEventReminder(testEvent);
        
        if (notificationResult.success) {
          const reminderDate = new Date(notificationResult.reminderTime);
          toast.success(`🔔 Reminder scheduled for ${reminderDate.toLocaleString()}`);
        } else {
          toast.warning('Calendar sync worked, but notification failed');
        }
      } else {
        toast.error(`❌ Calendar test failed: ${calendarResult.error}`);
      }
      
    } catch (error) {
      console.error('Test integration error:', error);
      toast.error('Integration test failed');
    } finally {
      setIsLoading(false);
    }
  };

  const StatusIcon = ({ status }) => {
    if (status) return <Check className="h-4 w-4 text-green-600" />;
    return <X className="h-4 w-4 text-red-600" />;
  };

  return (
    <Card className="app-card">
      <CardHeader className="app-card-header">
        <CardTitle className="flex items-center space-x-2">
          <Calendar className="h-5 w-5" />
          <span>Calendar & Notifications</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="app-card-content space-y-4">
        
        {/* Calendar Integration Status */}
        <div className="space-y-2">
          <h4 className="font-semibold text-gray-700 flex items-center space-x-2">
            <Calendar className="h-4 w-4" />
            <span>Device Calendar Integration</span>
          </h4>
          
          <div className="space-y-1 text-sm">
            <div className="flex items-center justify-between">
              <span>Platform Support:</span>
              <StatusIcon status={calendarStatus.isSupported} />
            </div>
            <div className="flex items-center justify-between">
              <span>Service Initialized:</span>
              <StatusIcon status={calendarStatus.isInitialized} />
            </div>
            <div className="flex items-center justify-between">
              <span>Permissions Granted:</span>
              <StatusIcon status={calendarStatus.permissions} />
            </div>
            
            {calendarStatus.calendarInfo && (
              <div className="mt-2 p-2 bg-amber-50 rounded text-xs">
                <div><strong>Default Calendar:</strong> {calendarStatus.calendarInfo.defaultCalendar || 'Not available'}</div>
                <div><strong>Devimana Calendar:</strong> {calendarStatus.calendarInfo.devimanaCalendar || 'Will be created'}</div>
              </div>
            )}
          </div>
        </div>

        {/* Notification Status */}
        <div className="space-y-2">
          <h4 className="font-semibold text-gray-700 flex items-center space-x-2">
            <Bell className="h-4 w-4" />
            <span>Notification Reminders</span>
          </h4>
          
          <div className="space-y-1 text-sm">
            <div className="flex items-center justify-between">
              <span>Platform Support:</span>
              <StatusIcon status={notificationStatus.isSupported} />
            </div>
            <div className="flex items-center justify-between">
              <span>Permissions Granted:</span>
              <StatusIcon status={notificationStatus.permissions} />
            </div>
          </div>
        </div>

        {/* Integration Info */}
        <div className="p-3 bg-blue-50 rounded-lg text-sm">
          <div className="flex items-start space-x-2">
            <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5" />
            <div>
              <div className="font-medium text-blue-800">How it works:</div>
              <ul className="text-blue-700 text-xs mt-1 space-y-1">
                <li>• Events sync with your device calendar</li>
                <li>• 24-hour reminders sent before events</li>
                <li>• Works with iOS Calendar & Google Calendar</li>
                <li>• Includes Tithi information in descriptions</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Test Integration Button */}
        {(calendarStatus.isSupported || notificationStatus.isSupported) && (
          <Button 
            onClick={testCalendarIntegration}
            disabled={isLoading}
            className="w-full app-button"
          >
            {isLoading ? 'Testing...' : 'Test Calendar Integration'}
          </Button>
        )}

        {/* Web Platform Message */}
        {!calendarStatus.isSupported && !notificationStatus.isSupported && (
          <div className="p-3 bg-gray-50 rounded-lg text-sm text-center">
            <div className="text-gray-600">
              Calendar sync and notifications are available on mobile devices.
              <br />
              Install the app on your phone to use these features.
            </div>
          </div>
        )}

      </CardContent>
    </Card>
  );
};

export default CalendarIntegration;